import React, { Fragment, useMemo } from 'react';
import Highcharts, { Point } from 'highcharts';
import { TimelineSeriesCustomData } from '../shared';
import { stringDateWithTimeFromUtcNonNullable } from '@utils/convertor';

interface PointWithMarker extends Point {
    status?: string;
    marker?: {
        symbol?: string;
    };
}

type CustomUserOptions = Highcharts.SeriesOptionsType & TimelineSeriesCustomData;
function getMaxRowsForHeight(chartHeight: number, pointCount: number) {
    const TOOLTIP_PADDING = 24;
    const POINT_ROW_HEIGHT = 19;
    const POINT_ROW_PADDING = 4;
    const DATE_HEIGHT = 15;
    const DATE_PADDING = 4;

    // Available height for points.
    // 16px = tooltip padding.
    // 15px = date footer.
    // 4px = spacing for date footer
    const heightForPoints = chartHeight - TOOLTIP_PADDING - DATE_HEIGHT - DATE_PADDING;

    // Not enough room for even 1 row...
    if (heightForPoints < POINT_ROW_HEIGHT) {
        return 0;
    }

    // No padding for 1st row. Padding for every subsequent row
    const rows = 1 + Math.floor((heightForPoints - POINT_ROW_HEIGHT) / (POINT_ROW_HEIGHT + POINT_ROW_PADDING));

    // If there are fewer rows than points, reduce rows by 1 (so an "and n more..." row can be added at end)
    return rows < pointCount ? rows - 1 : rows;
}

interface IProps {
    point: Point;
    basisDate?: Date;
    chartHeight: number;
}

const PointTooltip = ({ point, basisDate, chartHeight }: IProps) => {
    const points = (point.points?.length ?? 0) > 0 ? point.points! : [point];

    const maxPointsRows = getMaxRowsForHeight(chartHeight, points.length);

    const getDateWithDayFromX = (x: number) => {
        const dateTime = new Date(x);
        const dateText = stringDateWithTimeFromUtcNonNullable(dateTime, 'DD-MMM-YYYY')!;

        if (!basisDate) {
            return dateText;
        }
        // get difference in days from basis date
        const timeDiff = dateTime.getTime() - basisDate.getTime();
        const dayDiff = Math.floor(timeDiff / (1000 * 3600 * 24));

        return `${dateText} (Day ${dayDiff})`;
    };

    const x = useMemo(() => getDateWithDayFromX(point.x), [point.x]);

    const x2 = useMemo(() => {
        if (!point.x2) return undefined;

        return getDateWithDayFromX(point.x2);
    }, [point.x2]);

    return (
        <div className="timeline-point-tooltip">
            <div className="points">
                {points.slice(0, maxPointsRows).map((p: PointWithMarker) => {
                    // We will default to using the 'circle' svg icon if we can't determine a better one (currently only in the case of Visits, as
                    // determined by their "status").
                    let svgUrl = 'url(/highcharts-svg/circle.svg)';

                    const symbol = p.marker?.symbol;

                    const userOptions = p.series.userOptions as CustomUserOptions;

                    if (userOptions.isThreshold) {
                        svgUrl = 'url(/highcharts-svg/ellipsis.svg)';
                    } else if (symbol?.startsWith('url(') && symbol.endsWith('.svg)')) {
                        // If the symbol is already a url to an svg, use that.
                        svgUrl = symbol;
                    }

                    let nameOrValue = '';

                    if (p.name) {
                        nameOrValue = p.name;
                    } else if (userOptions.isFloat) {
                        nameOrValue = `${p.y?.toFixed(2) ?? ''}`;
                    } else {
                        nameOrValue = `${p.y}`;
                    }

                    if (userOptions.unit) {
                        nameOrValue += ` ${userOptions.unit}`;
                    }

                    /* FontAwesome Icons don't seem to work in a tooltip at this level. Even referencing embedded SVGs won't work, which
                    is why we're using img tags pointing to public/svg folder. Consequently, to recolour an svg added this way, we can't
                    just set the fill as we might prefer to; instead we use SVG as a mask and set the background colour of the img instead
                    to (hopefully) the same effect. */
                    const marker = (
                        <div
                            style={{
                                backgroundColor: p.color as string,
                                WebkitMask: `${svgUrl} no-repeat center`,
                                mask: `${svgUrl} no-repeat center`,
                                width: '14px',
                                height: '14px',
                                position: 'relative',
                                top: '1px',
                            }}
                        />
                    );

                    if (p.status) {
                        return (
                            <Fragment key={p.series.name}>
                                <div className="point-label">
                                    <b>{p.series.name}</b>
                                    <span>{nameOrValue}</span>
                                </div>

                                <div className="point-label">
                                    {marker}
                                    <span>{p.status}</span>
                                </div>
                            </Fragment>
                        );
                    }

                    return (
                        <Fragment key={p.series.name}>
                            <div className="point-label">
                                {marker}
                                <b>{p.series.name}</b>
                            </div>

                            <span>{nameOrValue}</span>
                        </Fragment>
                    );
                })}
            </div>
            {maxPointsRows < points.length && <div>and {points.length - maxPointsRows} more...</div>}

            <div className="dates">
                {x}
                {x2 && (
                    <>
                        <span className="to">to</span>
                        {x2}
                    </>
                )}
            </div>
        </div>
    );
};

export default PointTooltip;
